import { useMemo, useState } from 'react';
import {
  BarChart3,
  BookOpen,
  ClipboardList,
  GraduationCap,
  UserCheck,
  XCircle,
  AlertTriangle,
  Clock,
  Copy,
} from 'lucide-react';
import { useChamadas } from '../../api/hooks/useChamadas';
import { useHorarioAtual } from '../../api/hooks/useHorarios';
import type { Professor } from '../../types/models';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import { toast } from 'sonner';

function truncate(s: string, max: number) {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Painel lateral — modal editar professor */
export function ProfessorEditSidePanel({
  escolaId,
  professorId,
  professor,
  selectedMateriasCount,
}: {
  escolaId: string;
  professorId: string;
  professor?: Professor | null;
  selectedMateriasCount: number;
}) {
  const { horariosHoje } = useHorarioAtual(professorId);
  const { chamadas } = useChamadas({ professorId });

  const turmasGrade = useMemo(() => {
    const map = new Map<string, { nome: string; aulas: number }>();
    (horariosHoje || []).forEach((h: { turmaId?: string; turma?: { nome?: string } }) => {
      if (!h.turmaId || !h.turma) return;
      const cur = map.get(h.turmaId);
      if (cur) cur.aulas += 1;
      else map.set(h.turmaId, { nome: h.turma.nome || 'Turma', aulas: 1 });
    });
    return [...map.values()];
  }, [horariosHoje]);

  const chamadasConcluidas = chamadas.filter((c) => c.status === 'CONCLUIDA').length;
  const totalChamadas = chamadas.length;
  const pctAderencia =
    totalChamadas > 0 ? Math.round((chamadasConcluidas / totalChamadas) * 100) : 100;
  const aderenciaColor = pctAderencia >= 90 ? '#047857' : pctAderencia >= 70 ? '#b45309' : '#dc2626';

  const statusConta = String((professor as { statusConta?: string })?.statusConta ?? 'ATIVA');

  const [gerandoLink, setGerandoLink] = useState(false);
  const [newGeneratedLink, setNewGeneratedLink] = useState<string | null>(null);
  const handleRegerarLink = async () => {
    if (!escolaId || !professor) return;
    setGerandoLink(true);
    try {
      const { data } = await api.post(ROUTES.equipe.convidar(escolaId), {
        nome: professor.nome,
        email: professor.email,
        cargo: 'PROFESSOR',
        telefone: (professor as any).telefone || undefined,
        acessoDireto: true,
      });
      if (data.activationLink) {
        let activationLink = data.activationLink;
        try {
          const parsed = new URL(activationLink);
          activationLink = `${window.location.origin}${parsed.pathname}${parsed.search}`;
        } catch {}
        navigator.clipboard.writeText(activationLink);
        setNewGeneratedLink(activationLink);
        toast.success('Novo link gerado e copiado!');
      }
    } catch {
      toast.error('Erro ao gerar link.');
    } finally {
      setGerandoLink(false);
    }
  };

  return (
    <div className="ac-modal-dual__side ac-professor-edit-side">
      <div className="ac-modal-dual__side-panel">
        <div className="ac-modal-dual__side-head">
          <UserCheck size={14} strokeWidth={1.5} />
          <strong>Contexto na escola</strong>
        </div>
        <div className="ac-modal-dual__side-body">
          <div className="ac-professor-edit-kpis">
            <div className="ac-professor-edit-kpi">
              <BarChart3 size={12} style={{ color: aderenciaColor }} />
              <span>
                <strong style={{ color: aderenciaColor }}>{pctAderencia}%</strong> aderência chamadas
              </span>
            </div>
            <div className="ac-professor-edit-kpi">
              <BookOpen size={12} />
              <span>
                <strong>{selectedMateriasCount}</strong> disciplina(s) selecionada(s)
              </span>
            </div>
            <div className="ac-professor-edit-kpi">
              <GraduationCap size={12} />
              <span>
                <strong>{turmasGrade.length}</strong> turma(s) na grade hoje
              </span>
            </div>
            <div className="ac-professor-edit-kpi">
              <ClipboardList size={12} />
              <span>
                <strong>{chamadasConcluidas}/{totalChamadas}</strong> chamadas concluídas
              </span>
            </div>
          </div>

          <p className="ac-professor-edit-status">
            Conta: <strong>{statusConta}</strong>
            {professor?.ativo === false ? ' · vínculo inativo nesta escola' : ''}
          </p>

          {turmasGrade.length > 0 ? (
            <ul className="ac-professor-edit-turmas">
              {turmasGrade.map((t) => (
                <li key={t.nome}>
                  <span className="ac-professor-edit-turma-nome" title={t.nome}>
                    {truncate(t.nome, 28)}
                  </span>
                  <span className="ac-professor-edit-turma-aulas">{t.aulas} aula{t.aulas !== 1 ? 's' : ''}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="ac-professor-edit-empty">Nenhuma turma na grade de horários de hoje.</p>
          )}

          {/* Convite Pendente Box no Contexto */}
          {(() => {
            const status = (professor as any)?.statusConta;
            const isPendente = status === 'PENDENTE' || status === 'FALHOU';
            if (!isPendente) return null;
            const criadoEm = (professor as any)?.criadoEm ? new Date((professor as any).criadoEm) : null;
            const expiraEm = criadoEm ? new Date(criadoEm.getTime() + 72 * 60 * 60 * 1000) : null;
            const expirado = expiraEm ? new Date() > expiraEm : false;

            return (
              <div style={{
                padding: '12px', borderRadius: 10, marginTop: 20,
                background: status === 'FALHOU' ? 'rgba(239,68,68,0.06)' : expirado ? 'rgba(239,68,68,0.06)' : 'rgba(245,158,11,0.06)',
                border: `1px solid ${status === 'FALHOU' ? 'rgba(239,68,68,0.2)' : expirado ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)'}`,
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    {status === 'FALHOU' ? <XCircle size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} /> : expirado ? <AlertTriangle size={16} style={{ color: '#ef4444', flexShrink: 0, marginTop: 2 }} /> : <Clock size={16} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '.78rem', fontWeight: 700, lineHeight: 1.2, color: status === 'FALHOU' ? '#dc2626' : expirado ? '#991b1b' : '#92400e' }}>
                        {status === 'FALHOU' ? 'Email Falhou' : expirado ? 'Convite Expirado' : 'Convite Pendente'}
                      </div>
                      <div style={{ fontSize: '.7rem', lineHeight: 1.3, color: '#6b7280', marginTop: 2 }}>
                        {status === 'FALHOU'
                          ? 'O email foi rejeitado. Corrija e reenvie.'
                          : expirado ? 'O link expirou. Gere um novo link.' : 'Aguardando ativação pelo professor.'
                        }
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleRegerarLink}
                    disabled={gerandoLink}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px',
                      borderRadius: 8, border: '1px solid rgba(99,102,241,0.25)',
                      background: 'rgba(99,102,241,0.08)', color: '#6366f1',
                      fontSize: '.74rem', fontWeight: 700, cursor: 'pointer', width: '100%',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Copy size={12} />
                    {gerandoLink ? 'Gerando...' : expirado ? 'Gerar Novo Link' : 'Gerar Link e Copiar'}
                  </button>

                  {newGeneratedLink && (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6, marginTop: 4,
                      background: 'white', border: '1px solid #cbd5e1', borderRadius: 8,
                      padding: '6px 8px', width: '100%'
                    }}>
                      <input
                        type="text"
                        readOnly
                        value={newGeneratedLink}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                        style={{
                          flex: 1, fontSize: '0.68rem', background: 'transparent',
                          border: 'none', outline: 'none', color: '#475569',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(newGeneratedLink);
                          toast.success('Link copiado!');
                        }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#6366f1', padding: 2, display: 'flex', alignItems: 'center'
                        }}
                        title="Copiar Link"
                      >
                        <Copy size={11} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
